import React from 'react';
import { Box, Typography } from '@mui/material';
import ArgumentInput from './ArgumentInput';
import ArgumentSummary from './ArgumentSummary';
import JudgmentDisplay from './JudgmentDisplay';

interface ArgumentViewProps {
  argumentHook: {
    message: string;
    loading: boolean;
    userInput: string;
    setUserInput: (value: string) => void;
    argumentList: Array<{ content: string; role: string }>;
    argumentSummary: string;
    isFinalized: boolean;
    awaitingJudgment: boolean;
    judgment: string;
    hasReceivedResponse: boolean;
    handleUserInputSubmit: () => void;
    handleFinalizeArgument: () => void;
  };
  opponentName: string | null;
  opponentState: string | null;
  topic: string | null;
}

const ArgumentView: React.FC<ArgumentViewProps> = ({
  argumentHook,
  opponentName,
  opponentState,
  topic,
}) => {
  // Helper function to get opponent message
  const getOpponentMessage = () => {
    if (!opponentState || !opponentName) {
      return 'Waiting for your opponent to join the argument.';
    }
    switch (opponentState) {
      case 'created':
        return `${opponentName} has joined the argument.`;
      case 'in-process':
        return `${opponentName} is crafting their argument.`;
      case 'finalized':
        return 'Generating judgment...';
      default:
        return 'Fetching opponent status...';
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <ArgumentInput
        userInput={argumentHook.userInput}
        setUserInput={argumentHook.setUserInput}
        handleUserInputSubmit={argumentHook.handleUserInputSubmit}
        handleFinalizeArgument={argumentHook.handleFinalizeArgument}
        hasReceivedResponse={argumentHook.hasReceivedResponse}
        loading={argumentHook.loading}
        isFinalized={argumentHook.isFinalized}
      />

      <ArgumentSummary
        argumentSummary={argumentHook.argumentSummary}
        argumentList={argumentHook.argumentList}
        isFinalized={argumentHook.isFinalized}
      />

      {/* Opponent message shown after finalize but before judgment */}
      {argumentHook.isFinalized && !argumentHook.judgment && (
        <Typography variant="body1" sx={{ mt: 2 }}>
          {getOpponentMessage()}
        </Typography>
      )}

      <JudgmentDisplay
        isFinalized={argumentHook.isFinalized}
        awaitingJudgment={argumentHook.awaitingJudgment}
        judgment={argumentHook.judgment}
        topic={topic ?? 'Unknown Topic'}
      />
    </Box>
  );
};

export default ArgumentView;
